import type { Canvas } from "../../Canvas";
import { DrawableAssets, type DrawableAssetId } from "../../Assets";

export interface RectBounds {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface MetalBeveledBorderOptions extends RectBounds {
	radius?: number;
	bevel?: number;
	fill?: string;
	bodyColor?: string;
	outerStroke?: string;
	innerShadow?: string;
	copper?: string;
	highlight?: string;
	shadow?: string;
	lineScale?: number;
	scratches?: boolean;
	seed?: number;
}

const defaultStyle = {
	fill: "rgba(5,6,7,0.9)",
	bodyColor: "#2b302f",
	outerStroke: "#050403",
	innerShadow: "#080705",
	copper: "#b87333",
	highlight: "rgba(242,193,122,0.78)",
	shadow: "rgba(58,28,13,0.9)",
};

const metalHighlight = "rgba(226,226,212,0.52)";
const metalMidHighlight = "rgba(154,151,134,0.32)";
const gunmetalTexture = "/images/assets/card-border/dark-gunmetal-scratched.png";
const copperTexture = "/images/assets/card-border/aged-copper-satin.png";
export const boardCardFrameAsset = "/images/assets/card-frame/board-card-frame.png";
export const iconPanelOctagonAsset = "/images/assets/card-frame/icon-panel-octagon.png";
export const edgePanelHorizontalAsset = "/images/assets/card-frame/edge-panel-horizontal.png";

export const METAL_BORDER_TEXTURE_ASSETS = [
	gunmetalTexture,
	copperTexture,
	boardCardFrameAsset,
	iconPanelOctagonAsset,
	edgePanelHorizontalAsset,
] as const;

export const drawBorderAsset = (
	document: Canvas,
	asset: string,
	x: number,
	y: number,
	width: number,
	height: number,
): void => {
	const loaded = DrawableAssets.get(asset as DrawableAssetId);
	document.image((loaded ?? asset) as string | ImageBitmap, x, y, {
		width,
		height,
	});
};

export const drawStraightPanelAsset = (
	document: Canvas,
	x: number,
	y: number,
	width: number,
	height: number,
	asset: string = edgePanelHorizontalAsset,
): void => {
	if (width <= height) {
		drawBorderAsset(document, asset, x, y, width, height);
		return;
	}

	document.save();
	document.translate(x + width, y);
	document.rotate(Math.PI / 2);
	drawBorderAsset(document, asset, 0, 0, height, width);
	document.restore();
};

const seeded = (seed: number, index: number): number => {
	const value = Math.sin((seed + 1) * 19.19 + (index + 1) * 78.233) * 43758.5453;
	return value - Math.floor(value);
};

export const octagonPoints = (
	x: number,
	y: number,
	width: number,
	height: number,
	bevel: number,
): Array<{ x: number; y: number }> => {
	const cut = Math.max(0, Math.min(bevel, width / 2, height / 2));
	return [
		{ x: x + cut, y },
		{ x: x + width - cut, y },
		{ x: x + width, y: y + cut },
		{ x: x + width, y: y + height - cut },
		{ x: x + width - cut, y: y + height },
		{ x: x + cut, y: y + height },
		{ x, y: y + height - cut },
		{ x, y: y + cut },
	];
};

const strokePolyline = (
	document: Canvas,
	points: Array<{ x: number; y: number }>,
	close = false,
): void => {
	if (points.length === 0) {
		return;
	}
	document.moveTo(points[0].x, points[0].y);
	for (let index = 1; index < points.length; index += 1) {
		document.lineTo(points[index].x, points[index].y);
	}
	if (close) {
		document.closePath();
	}
	document.stroke();
};

const lerpPoint = (
	from: { x: number; y: number },
	to: { x: number; y: number },
	amount: number,
): { x: number; y: number } => ({
	x: from.x + (to.x - from.x) * amount,
	y: from.y + (to.y - from.y) * amount,
});

const inset = (bounds: RectBounds, amount: number): RectBounds => ({
	x: bounds.x + amount,
	y: bounds.y + amount,
	width: Math.max(0, bounds.width - amount * 2),
	height: Math.max(0, bounds.height - amount * 2),
});

export const drawMetalBeveledRoundedRect = (
	document: Canvas,
	options: MetalBeveledBorderOptions,
): void => {
	const style = { ...defaultStyle, ...options };
	const lineScale = options.lineScale ?? Math.min(options.width, options.height);
	const bodyWidth = Math.max(3, lineScale * 0.043);
	const shadowWidth = Math.max(1, lineScale * 0.014);
	const copperWidth = Math.max(1.2, lineScale * 0.01);
	const highlightWidth = Math.max(1, lineScale * 0.005);
	const radius = options.radius ?? Math.min(options.width, options.height) * 0.075;
	const outer = inset(options, bodyWidth * 0.5);
	const recessed = inset(options, bodyWidth * 0.96);
	const copper = inset(options, bodyWidth * 1.38);

	document.save();
	document
		.lineWidth(bodyWidth)
		.strokeColor(style.bodyColor)
		.roundedRect(outer.x, outer.y, outer.width, outer.height, Math.max(0, radius - bodyWidth * 0.5))
		.stroke();
	drawTexturedRoundedBorder(document, outer, bodyWidth * 0.82, radius, gunmetalTexture, 0.88);
	document
		.lineWidth(Math.max(1, bodyWidth * 0.18))
		.strokeColor(style.outerStroke)
		.roundedRect(outer.x, outer.y, outer.width, outer.height, Math.max(0, radius - bodyWidth * 0.5))
		.stroke();
	drawRoundedFrameShine(document, outer, radius, Math.max(1, bodyWidth * 0.13), metalHighlight);
	drawRoundedFrameShadow(document, outer, radius, Math.max(1, bodyWidth * 0.18), "#050403");
	document
		.lineWidth(shadowWidth)
		.strokeColor(style.innerShadow)
		.roundedRect(
			recessed.x,
			recessed.y,
			recessed.width,
			recessed.height,
			Math.max(0, radius - bodyWidth),
		)
		.stroke();
	document
		.lineWidth(copperWidth)
		.strokeColor(style.copper)
		.roundedRect(
			copper.x,
			copper.y,
			copper.width,
			copper.height,
			Math.max(0, radius - bodyWidth * 1.4),
		)
		.stroke();
	drawTexturedRoundedBorder(document, copper, copperWidth * 2.2, radius, copperTexture, 0.62);

	document
		.lineWidth(highlightWidth)
		.strokeColor(style.highlight)
		.moveTo(copper.x + copper.width * 0.12, copper.y)
		.lineTo(copper.x + copper.width * 0.82, copper.y)
		.stroke();
	document
		.lineWidth(highlightWidth)
		.strokeColor(style.highlight)
		.moveTo(copper.x, copper.y + copper.height * 0.12)
		.lineTo(copper.x, copper.y + copper.height * 0.82)
		.stroke();
	document
		.lineWidth(highlightWidth)
		.strokeColor(style.shadow)
		.moveTo(copper.x + copper.width, copper.y + copper.height * 0.18)
		.lineTo(copper.x + copper.width, copper.y + copper.height * 0.9)
		.stroke();
	document
		.lineWidth(highlightWidth)
		.strokeColor(style.shadow)
		.moveTo(copper.x + copper.width * 0.18, copper.y + copper.height)
		.lineTo(copper.x + copper.width * 0.9, copper.y + copper.height)
		.stroke();

	if (options.scratches) {
		drawBorderScratches(
			document,
			copper,
			options.seed ?? 1,
			lineScale,
			Math.max(bodyWidth * 0.75, copperWidth),
		);
	}
	document.restore();
};

export const drawMetalBeveledOctagon = (
	document: Canvas,
	options: MetalBeveledBorderOptions,
): void => {
	const style = { ...defaultStyle, ...options };
	const lineScale = options.lineScale ?? Math.min(options.width, options.height);
	const bodyWidth = Math.max(2, lineScale * 0.072);
	const copperWidth = Math.max(1.15, lineScale * 0.027);
	const fineWidth = Math.max(0.85, lineScale * 0.01);
	const bevel = options.bevel ?? Math.min(options.width, options.height) * 0.26;
	const outerPoints = octagonPoints(options.x, options.y, options.width, options.height, bevel);
	const recessed = inset(options, bodyWidth * 0.78);
	const recessedPoints = octagonPoints(
		recessed.x,
		recessed.y,
		recessed.width,
		recessed.height,
		Math.max(0, bevel - bodyWidth * 0.56),
	);
	const copper = inset(options, bodyWidth * 1.34);
	const copperPoints = octagonPoints(
		copper.x,
		copper.y,
		copper.width,
		copper.height,
		Math.max(0, bevel - bodyWidth),
	);
	const copperInterior = inset(copper, copperWidth * 1.35);
	const copperInteriorPoints = octagonPoints(
		copperInterior.x,
		copperInterior.y,
		copperInterior.width,
		copperInterior.height,
		Math.max(0, bevel - bodyWidth - copperWidth),
	);

	document.save();
	document.fillColor(style.bodyColor).polygon(outerPoints).fill();
	drawTexturedPolygon(document, outerPoints, options, gunmetalTexture, 0.9);
	drawTexturedMetalFacet(document, [
		outerPoints[0],
		lerpPoint(outerPoints[0], outerPoints[1], 0.32),
		lerpPoint(recessedPoints[0], recessedPoints[1], 0.32),
		recessedPoints[0],
	]);
	drawTexturedMetalFacet(document, [
		lerpPoint(outerPoints[0], outerPoints[1], 0.72),
		outerPoints[1],
		outerPoints[2],
		recessedPoints[2],
		recessedPoints[1],
		lerpPoint(recessedPoints[0], recessedPoints[1], 0.72),
	]);
	document
		.lineWidth(Math.max(1, fineWidth * 1.4))
		.strokeColor(style.outerStroke)
		.polygon(outerPoints)
		.stroke();
	document.lineWidth(Math.max(1, fineWidth * 1.45)).strokeColor(metalHighlight);
	strokePolyline(document, [
		outerPoints[7],
		outerPoints[0],
		lerpPoint(outerPoints[0], outerPoints[1], 0.28),
	]);
	document.lineWidth(Math.max(1, fineWidth * 0.8)).strokeColor(metalMidHighlight);
	strokePolyline(document, [outerPoints[0], outerPoints[1], outerPoints[2]]);
	document.lineWidth(Math.max(1, fineWidth * 1.2)).strokeColor("rgba(226,226,212,0.58)");
	strokePolyline(document, [
		lerpPoint(outerPoints[0], outerPoints[1], 0.68),
		outerPoints[1],
		lerpPoint(outerPoints[1], outerPoints[2], 0.42),
	]);
	document.lineWidth(Math.max(1, fineWidth * 1.25)).strokeColor("#050403");
	strokePolyline(document, [outerPoints[2], outerPoints[3], outerPoints[4]]);
	document.fillColor(style.fill).polygon(recessedPoints).fill();
	document
		.lineWidth(Math.max(1, fineWidth * 1.25))
		.strokeColor(style.innerShadow)
		.polygon(recessedPoints)
		.stroke();
	drawTexturedPolygon(document, copperPoints, copper, copperTexture, 0.78);
	document.fillColor(style.fill).polygon(copperInteriorPoints).fill();
	document
		.lineWidth(Math.max(1, copperWidth * 1.25))
		.strokeColor("#5f3218")
		.polygon(copperPoints)
		.stroke();
	document.lineWidth(copperWidth).strokeColor(style.copper).polygon(copperPoints).stroke();
	document.lineWidth(fineWidth).strokeColor(style.highlight);
	strokePolyline(document, [copperPoints[6], copperPoints[7], copperPoints[0], copperPoints[1]]);
	document.lineWidth(Math.max(1, fineWidth * 0.7)).strokeColor("rgba(255,216,150,0.9)");
	strokePolyline(document, [
		lerpPoint(copperPoints[7], copperPoints[0], 0.5),
		copperPoints[0],
		lerpPoint(copperPoints[0], copperPoints[1], 0.45),
	]);
	document.lineWidth(fineWidth).strokeColor(style.shadow);
	strokePolyline(document, [copperPoints[2], copperPoints[3], copperPoints[4], copperPoints[5]]);

	if (options.scratches) {
		document.save();
		document.polygon(copperPoints).clip();
		drawScratches(document, copper, options.seed ?? 1, lineScale);
		document.restore();
	}
	document.restore();
};

const drawTexturedMetalFacet = (
	document: Canvas,
	points: Array<{ x: number; y: number }>,
): void => {
	const minX = Math.min(...points.map((point) => point.x));
	const minY = Math.min(...points.map((point) => point.y));
	const maxX = Math.max(...points.map((point) => point.x));
	const maxY = Math.max(...points.map((point) => point.y));
	const rect = {
		x: minX,
		y: minY,
		width: maxX - minX,
		height: maxY - minY,
	};

	document.save();
	document.fillColor("rgba(91,94,86,0.72)").polygon(points).fill();
	document.polygon(points).clip();
	document.opacity(0.55);
	drawBorderAsset(document, gunmetalTexture, rect.x, rect.y, rect.width, rect.height);
	document.restore();
};

const drawTexturedPolygon = (
	document: Canvas,
	points: Array<{ x: number; y: number }>,
	bounds: RectBounds,
	texture: string,
	alpha: number,
): void => {
	document.save();
	document.polygon(points).clip();
	document.opacity(alpha);
	drawBorderAsset(document, texture, bounds.x, bounds.y, bounds.width, bounds.height);
	document.restore();
};

const drawTexturedRoundedBorder = (
	document: Canvas,
	bounds: RectBounds,
	width: number,
	radius: number,
	texture: string,
	alpha: number,
): void => {
	const strip = Math.max(1, width);
	document.save();
	document.roundedRect(bounds.x, bounds.y, bounds.width, bounds.height, radius).clip();
	document.opacity(alpha);
	drawBorderAsset(document, texture, bounds.x, bounds.y, bounds.width, strip);
	drawBorderAsset(document, texture, bounds.x, bounds.y, strip, bounds.height);
	drawBorderAsset(
		document,
		texture,
		bounds.x + bounds.width - strip,
		bounds.y,
		strip,
		bounds.height,
	);
	drawBorderAsset(
		document,
		texture,
		bounds.x,
		bounds.y + bounds.height - strip,
		bounds.width,
		strip,
	);
	document.restore();
};

const drawRoundedFrameShine = (
	document: Canvas,
	bounds: RectBounds,
	radius: number,
	width: number,
	color: string,
): void => {
	document.lineWidth(width).strokeColor(color);
	document
		.moveTo(bounds.x + radius * 0.85, bounds.y)
		.lineTo(bounds.x + bounds.width * 0.27, bounds.y)
		.stroke();
	document
		.moveTo(bounds.x, bounds.y + radius * 0.9)
		.lineTo(bounds.x, bounds.y + bounds.height * 0.34)
		.stroke();
	document
		.moveTo(bounds.x + bounds.width * 0.76, bounds.y)
		.lineTo(bounds.x + bounds.width * 0.85, bounds.y)
		.stroke();
};

const drawRoundedFrameShadow = (
	document: Canvas,
	bounds: RectBounds,
	radius: number,
	width: number,
	color: string,
): void => {
	document.lineWidth(width).strokeColor(color);
	document
		.moveTo(bounds.x + bounds.width, bounds.y + radius)
		.lineTo(bounds.x + bounds.width, bounds.y + bounds.height * 0.58)
		.stroke();
	document
		.moveTo(bounds.x + bounds.width * 0.55, bounds.y + bounds.height)
		.lineTo(bounds.x + bounds.width - radius, bounds.y + bounds.height)
		.stroke();
};

const drawBorderScratches = (
	document: Canvas,
	bounds: RectBounds,
	seed: number,
	lineScale: number,
	borderWidth: number,
): void => {
	const strips: RectBounds[] = [
		{ x: bounds.x, y: bounds.y, width: bounds.width, height: borderWidth },
		{
			x: bounds.x + bounds.width - borderWidth,
			y: bounds.y,
			width: borderWidth,
			height: bounds.height,
		},
		{
			x: bounds.x,
			y: bounds.y + bounds.height - borderWidth,
			width: bounds.width,
			height: borderWidth,
		},
		{ x: bounds.x, y: bounds.y, width: borderWidth, height: bounds.height },
	];

	document.save();
	for (let index = 0; index < strips.length; index += 1) {
		drawScratches(document, strips[index], seed + index * 13, lineScale, 3);
	}
	document.restore();
};

const drawScratches = (
	document: Canvas,
	bounds: RectBounds,
	seed: number,
	lineScale: number,
	count = 10,
): void => {
	document.save();
	document.opacity(0.08);
	document.lineWidth(Math.max(0.5, lineScale * 0.002));
	document.strokeColor("#f2c17a");
	for (let index = 0; index < count; index += 1) {
		const startX = bounds.x + seeded(seed, index * 4) * bounds.width;
		const startY = bounds.y + seeded(seed, index * 4 + 1) * bounds.height;
		const length = lineScale * (0.012 + seeded(seed, index * 4 + 2) * 0.022);
		const angle = -0.8 + seeded(seed, index * 4 + 3) * 1.6;
		document
			.moveTo(startX, startY)
			.lineTo(startX + Math.cos(angle) * length, startY + Math.sin(angle) * length)
			.stroke();
	}
	document.restore();
};
